import { MODULE_MANIFESTS } from "@/app/router/module-manifests";
import type { ModuleManifest, RoleClaim } from "@/types/clm-web";

const ROLE_ALIASES: Record<string, RoleClaim> = {
  authenticated: "authenticated",
  user: "authenticated",
  owner: "owner",
  super_admin: "super_admin",
  superadmin: "super_admin",
  admin: "admin",
  administrator: "admin",
  // CLMS-116 carried admin operator tiers (T059): projected role names from
  // the admin directory must not be dropped by normalization.
  admin_support_staff: "admin_support_staff",
  membership_coordinator: "membership_coordinator",
  // CLMS-158 (004-6 D17): Courses Admin is an operator-tier admin role whose
  // courses permissions are derived below, never carried as a raw claim alias.
  courses_admin: "courses_admin",
  // 011-CLMS-237 CR-006: content-only operator tiers (no derived permissions).
  financial_controller: "financial_controller",
  content_manager: "content_manager",
  member: "member",
  standard_member: "member",
  standardmember: "member",
  financial_read: "financial:read",
  financial: "financial:read",
  directory_read: "directory:read",
  industry_resource_directory_read: "directory:read",
  jobs_read: "jobs:read",
  ucc_read: "ucc:read",
  events_read: "events:read",
  compliance_read: "compliance:read",
  documents_read: "documents:read",
  search_read: "search:read",
};

const COURSES_PERMISSIONS: readonly RoleClaim[] = [
  "courses:view",
  "courses:manage",
  "courses:extend",
];

/**
 * 004-6 D17 role → courses permission map, mirrored from the server for
 * navigation gating only (the server stays authoritative). Membership
 * Coordinator, Owner and Super Admin derive no courses permission.
 */
export const COURSES_PERMISSION_ROLE_CLAIMS: Readonly<
  Partial<Record<RoleClaim, readonly RoleClaim[]>>
> = {
  admin: COURSES_PERMISSIONS,
  admin_support_staff: COURSES_PERMISSIONS,
  courses_admin: COURSES_PERMISSIONS,
};

export function normalizeRoleClaims(roles: readonly string[] = []): RoleClaim[] {
  const normalized = new Set<RoleClaim>();

  for (const role of roles) {
    const canonical = role
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");
    const claim = Object.hasOwn(ROLE_ALIASES, canonical) ? ROLE_ALIASES[canonical] : undefined;

    if (claim) {
      normalized.add(claim);
    } else if (canonical.includes(":")) {
      normalized.add(canonical as RoleClaim);
    }
  }

  for (const [roleClaim, permissions] of Object.entries(COURSES_PERMISSION_ROLE_CLAIMS)) {
    if (normalized.has(roleClaim as RoleClaim)) {
      for (const permission of permissions ?? []) {
        normalized.add(permission);
      }
    }
  }

  if (normalized.size > 0 && !normalized.has("anonymous")) {
    normalized.add("authenticated");
  }

  return [...normalized];
}

export function hasRole(requiredRole: RoleClaim, roleClaims: readonly RoleClaim[]): boolean {
  if (requiredRole === "anonymous") {
    return true;
  }

  if (requiredRole === "authenticated") {
    return roleClaims.length > 0 && !roleClaims.includes("anonymous");
  }

  if (requiredRole === "admin") {
    return (
      roleClaims.includes("admin") ||
      roleClaims.includes("owner") ||
      roleClaims.includes("super_admin")
    );
  }

  if (requiredRole === "owner") {
    return roleClaims.includes("owner");
  }

  return roleClaims.includes(requiredRole);
}

export function hasAnyRequiredRole(
  requiredRoles: readonly RoleClaim[],
  roleClaims: readonly RoleClaim[],
): boolean {
  if (requiredRoles.length === 0) {
    return false;
  }

  return requiredRoles.some((requiredRole) => hasRole(requiredRole, roleClaims));
}

/**
 * Literal claim matching with none of hasRole's expansions: requiring "admin"
 * here matches only the projected admin claim, never owner/super_admin. Used
 * by surfaces whose server gate is a literal operator claim (e.g. identities
 * read/list is Admin-only), where the escalation expansion would admit an
 * actor the server then 403s.
 */
export function hasAnyLiteralRole(
  requiredRoles: readonly RoleClaim[],
  roleClaims: readonly RoleClaim[],
): boolean {
  if (requiredRoles.length === 0) {
    return false;
  }

  return requiredRoles.some((requiredRole) => roleClaims.includes(requiredRole));
}

export function canAccessModule(
  manifest: ModuleManifest,
  roleClaims: readonly RoleClaim[],
): boolean {
  const admits = manifest.roleMatch === "literal" ? hasAnyLiteralRole : hasAnyRequiredRole;
  return admits(manifest.requiredRoles, roleClaims);
}

export function getAccessibleModuleManifests(
  roleClaims: readonly RoleClaim[],
): readonly ModuleManifest[] {
  return MODULE_MANIFESTS.filter(
    (manifest) => manifest.protected && canAccessModule(manifest, roleClaims),
  );
}
